import { createSelector } from '@reduxjs/toolkit';
import { functionsNotDeleted } from 'src/store/selectors';
import type { FunctionViewModel } from '@trinnvis/dabih-api-client';
import { functionIds } from 'src/store/selectors/functions/function-ids';

/**
 * Sorts an array ('array'), first by an array of numbers or strings ('primarySortArray')
 * that refers to values in 'primarySortField', and then ascending by a string field ('secondarySortField').
 * An item matching the value of 'lastId' is placed at the end of the returned array.
 * Written to sort functions and tasks by function rank, but can be used for other purposes.
 */
export function sortByArrayAndField<T>(
  array: T[],
  primarySortArray: unknown[],
  primarySortField: string,
  secondarySortField: string,
  lastId: string | number,
): T[] {
  const result = array
    .filter((f) => f[primarySortField] !== lastId)
    .sort((a, b) => {
      let indexA = primarySortArray.indexOf(a[primarySortField] ?? 0);
      let indexB = primarySortArray.indexOf(b[primarySortField] ?? 0);
      if (indexA === -1 && indexB === -1) {
        return (a[secondarySortField] ?? '').localeCompare(b[secondarySortField] ?? '');
      }
      if (indexA === -1) indexA = Infinity;
      if (indexB === -1) indexB = Infinity;
      return indexA - indexB;
    });
  const common = array.find((f) => f[primarySortField] === lastId);
  if (common) {
    result.push(common);
  }
  return result;
}

export const functionsByRank = createSelector(functionsNotDeleted, function (functions): FunctionViewModel[] {
  const idArray: number[] = Object.values(functionIds);
  return sortByArrayAndField(functions, idArray, 'templateId', 'name', functionIds.common);
});

export const functionUuidsByRank = createSelector(functionsNotDeleted, function (functions): string[] {
  const result: string[] = [];
  const idArray: number[] = Object.values(functionIds);
  idArray.forEach((id) => {
    const func = functions.find((f) => f.mainTemplateId === id);
    if (func) {
      result.push(func.uuid);
    }
  });
  return result;
});
